/**
 * AI caps admin API client (Phase X.3.7c C1b).
 *
 * Wraps the two endpoints under /v1/admin-review/ai-caps/. Owner/admin-gated
 * server-side (requireCapability('manage_team')); the rail visibility is
 * convenience, not security. Mirrors src/lib/aiUsage.ts / staffAdmin.ts.
 */

import { writeFetch } from './writeFetch';
import { apiErrorFrom } from './api';

export type CmCapSource = 'override' | 'tenant' | 'fallback';

/**
 * Where a person's own limit stands on the practice's today, decided on the
 * server by the same rule as the gate: none (no row), in_effect (ends today
 * or later), ended (ended before today), no_end_date (saved before the end
 * date existed; honoured until next saved).
 */
export type OverrideStatus = 'none' | 'in_effect' | 'ended' | 'no_end_date';

/** The date box's pre-fill for a new, ended or undated limit: today plus this. */
export const DEFAULT_OVERRIDE_DAYS = 30;

/**
 * The furthest a limit's last day may sit after today (the date box's max).
 * MIRROR: api/src/functions/aiCaps.ts MAX_OVERRIDE_DAYS, the server's rule.
 */
export const MAX_OVERRIDE_DAYS = 366;

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A YYYY-MM-DD day plus n days, on the string's parts through Date.UTC (no
 * local Date, so the viewer's zone never moves the day). Null for a string
 * that is not a real calendar day.
 */
export function addDaysIso(iso: string, n: number): string | null {
  const m = YMD_RE.exec(iso);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const base = new Date(Date.UTC(y, mo - 1, d));
  if (base.getUTCFullYear() !== y || base.getUTCMonth() !== mo - 1 || base.getUTCDate() !== d) return null;
  const out = new Date(Date.UTC(y, mo - 1, d + n));
  const yy = String(out.getUTCFullYear()).padStart(4, '0');
  const mm = String(out.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(out.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** "21 Nov 2026": day, short month and year, from the string's own parts. */
export function formatEndsOn(iso: string): string {
  const m = YMD_RE.exec(iso);
  if (!m) return iso;
  const month = MONTHS[Number(m[2]) - 1];
  if (month === undefined) return iso;
  return `${Number(m[3])} ${month} ${m[1]}`;
}

export interface CmCapAdminRow {
  user_id: string;
  display_name: string | null;
  email: string | null;
  role: string;
  /** Owners are exempt from caps (enforced server-side). */
  exempt: boolean;
  /** Drafting calls in the window (what the cap counts). */
  daily_used: number;
  weekly_used: number;
  /** Note-read model calls in the window (what the read allowance counts). */
  daily_read_used: number;
  weekly_read_used: number;
  daily_cap_override: number | null;
  weekly_cap_override: number | null;
  daily_cap_effective: number;
  weekly_cap_effective: number;
  daily_source: CmCapSource;
  weekly_source: CmCapSource;
  /** Refusals of every kind in the trailing 24 hours and 7 days (the Blocked column). */
  daily_blocks: number;
  weekly_blocks: number;
  /** The own limit's last day (YYYY-MM-DD); null with no row or no end date. */
  ends_on: string | null;
  override_status: OverrideStatus;
}

export interface CmCapsResponse {
  rows: CmCapAdminRow[];
  /** The practice's YYYY-MM-DD the statuses were decided on (the date box's min). */
  today: string;
  tenant_defaults: { daily_cap: number | null; weekly_cap: number | null };
  fallback: { daily_cap: number; weekly_cap: number };
  /** The tenant-wide note-read allowance the read guard enforces. */
  read_caps: { daily_cap: number; weekly_cap: number };
  request_id?: string;
}

/**
 * Override payload. null for a window = inherit (tenant default -> fallback).
 * ends_on is the last day the limit applies, required with a cap; null when
 * both caps are null (the limit is cleared).
 */
export interface CmCapOverrideInput {
  daily_cap: number | null;
  weekly_cap: number | null;
  ends_on: string | null;
}

async function requestJson(input: string, init: RequestInit = {}): Promise<unknown> {
  const r = await writeFetch(input, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!r.ok) throw await apiErrorFrom(r, `${init.method ?? 'GET'} ${input}`);
  return r.json();
}

const base = (slug: string): string =>
  `/api/t/${encodeURIComponent(slug)}/v1/admin-review/ai-caps`;

export const aiCapsApi = {
  async list(tenantSlug: string, signal?: AbortSignal): Promise<CmCapsResponse> {
    return (await requestJson(base(tenantSlug), { signal })) as CmCapsResponse;
  },

  async setCap(
    tenantSlug: string,
    userId: string,
    caps: CmCapOverrideInput,
  ): Promise<{ saved: true }> {
    return (await requestJson(`${base(tenantSlug)}/${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: JSON.stringify(caps),
    })) as { saved: true };
  },
};
